import React, { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from "react-native";
import { Plus, Trash, X } from "@phosphor-icons/react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";

import type { McqQuestion } from "../../../../graphql/generated/graphql";
import { useCreateMcqQuestionMutation, useUpdateMcqQuestionMutation } from "../../../../graphql/generated/graphql";
import { useToast } from "../../../../contexts/Toast/ToastProvider";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";

type McqQuestionEditorMode = "create" | "edit";

const DEFAULT_CHOICES = ["", "", "", ""];
const MIN_CHOICES = 2;

type McqQuestionEditorForm = {
  stem: string;
  choices: string[];
  correctChoiceIndex: number | null;
  explanation: string;
  points: string;
};

type McqQuestionEditorModalProps = {
  visible: boolean;
  mode: McqQuestionEditorMode;
  assessmentId?: string;
  question?: McqQuestion | null;
  nextOrder?: number;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
};

const optionLabel = (index: number) => String.fromCharCode(65 + index);

const McqQuestionEditorModal = ({
  visible,
  mode,
  assessmentId,
  question,
  nextOrder,
  onClose,
  onSaved,
}: McqQuestionEditorModalProps) => {
  const { showToast } = useToast();
  const [createMcqQuestion, { loading: isCreating }] = useCreateMcqQuestionMutation();
  const [updateMcqQuestion, { loading: isUpdating }] = useUpdateMcqQuestionMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { control, reset, setValue, handleSubmit, getValues } = useForm<McqQuestionEditorForm>({
    defaultValues: {
      stem: "",
      choices: DEFAULT_CHOICES,
      correctChoiceIndex: 0,
      explanation: "",
      points: "",
    },
  });

  const {
    fields: choiceFields,
    append: appendChoice,
    remove: removeChoice,
    replace: replaceChoices,
  } = useFieldArray({
    control,
    name: "choices",
  });

  const stem = useWatch({ control, name: "stem" });
  const choices = useWatch({ control, name: "choices" }) ?? [];
  const correctChoiceIndex = useWatch({ control, name: "correctChoiceIndex" });

  const canSubmit = useMemo(() => {
    const hasStem = Boolean(stem?.trim());
    const normalizedChoices = choices.map((choice) => choice?.trim());
    return hasStem && normalizedChoices.length >= MIN_CHOICES && correctChoiceIndex !== null;
  }, [stem, choices, correctChoiceIndex]);

  const isSubmitting = isCreating || isUpdating;
  const isEditMode = mode === "edit";
  const modalTitle = isEditMode ? "Edit Question" : "Add Question";
  const submitText = isEditMode ? "Save Question" : "Add Question";

  useEffect(() => {
    if (!visible) {
      return;
    }

    if (isEditMode && question) {
      reset({
        stem: question.stem ?? "",
        choices: question.choices?.length ? [...question.choices] : DEFAULT_CHOICES,
        correctChoiceIndex:
          typeof question.correctChoiceIndex === "number" && question.correctChoiceIndex >= 0
            ? question.correctChoiceIndex
            : null,
        explanation: question.explanation ?? "",
        points: typeof question.points === "number" ? String(question.points) : "",
      });

      replaceChoices(question.choices?.length ? [...question.choices] : DEFAULT_CHOICES);
    } else {
      const defaultTutorChoiceIndex = 0;
      reset({
        stem: "",
        choices: DEFAULT_CHOICES,
        correctChoiceIndex: defaultTutorChoiceIndex,
        explanation: "",
        points: "",
      });
      replaceChoices(DEFAULT_CHOICES);
    }

    setErrorMessage(null);
  }, [visible, isEditMode, question, reset, replaceChoices]);

  const handleAddOption = () => {
    appendChoice("");
    setErrorMessage(null);
  };

  const handleRemoveOption = (index: number) => {
    if (choiceFields.length <= MIN_CHOICES) {
      return;
    }

    removeChoice(index);
    setErrorMessage(null);

    const currentCorrectChoiceIndex = getValues("correctChoiceIndex");

    if (currentCorrectChoiceIndex === null) {
      return;
    }

    if (currentCorrectChoiceIndex === index) {
      setValue("correctChoiceIndex", null);
      return;
    }

    if (currentCorrectChoiceIndex > index) {
      setValue("correctChoiceIndex", currentCorrectChoiceIndex - 1);
    }
  };

  const onSubmit = async (data: McqQuestionEditorForm) => {
    setErrorMessage(null);

    if (!assessmentId) {
      setErrorMessage("Assessment ID is missing. Please close and reopen the question bank.");
      return;
    }

    const normalizedStem = data.stem.trim();
    const normalizedChoices = data.choices.map((choice) => choice.trim());
    const normalizedExplanation = data.explanation.trim();

    if (!normalizedStem) {
      setErrorMessage("Question is required.");
      return;
    }

    if (normalizedChoices.length < MIN_CHOICES || normalizedChoices.some((choice) => !choice)) {
      setErrorMessage("Please provide at least two non-empty answer options.");
      return;
    }

    if (
      data.correctChoiceIndex === null ||
      data.correctChoiceIndex < 0 ||
      data.correctChoiceIndex >= normalizedChoices.length ||
      !Number.isInteger(data.correctChoiceIndex)
    ) {
      setErrorMessage("Please select the correct answer.");
      return;
    }

    let normalizedPoints: number | undefined;
    if (data.points.trim()) {
      const parsedPoints = Number(data.points.trim());
      if (!Number.isFinite(parsedPoints) || parsedPoints <= 0) {
        setErrorMessage("Points must be a positive number.");
        return;
      }
      normalizedPoints = parsedPoints;
    }

    try {
      if (isEditMode) {
        if (!question?.id) {
          setErrorMessage("Question ID is missing for edit.");
          return;
        }

        const { data } = await updateMcqQuestion({
          variables: {
            mcqQuestionId: question.id,
            input: {
              stem: normalizedStem,
              choices: normalizedChoices,
              correctChoiceIndex: data.correctChoiceIndex,
              explanation: normalizedExplanation,
              points: normalizedPoints,
              order: question.order,
            },
          },
        });

        const updateResponse = data?.updateMcqQuestion;
        if (!updateResponse?.success) {
          setErrorMessage(updateResponse?.message || "Failed to update question.");
          return;
        }
      } else {
        const { data } = await createMcqQuestion({
          variables: {
            input: {
              assessmentId,
              stem: normalizedStem,
              choices: normalizedChoices,
              correctChoiceIndex: data.correctChoiceIndex,
              explanation: normalizedExplanation,
              points: normalizedPoints,
              order: nextOrder,
            },
          },
        });

        const createResponse = data?.createMcqQuestion;
        if (!createResponse?.success) {
          setErrorMessage(createResponse?.message || "Failed to create question.");
          return;
        }
      }

      await onSaved();

      showToast({
        message: isEditMode ? "Question updated successfully." : "Question added successfully.",
        type: "success",
      });
      onClose();
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Something went wrong. Please try again.");
    }
  };

  const handleCorrectChoiceSelect = (index: number) => {
    setValue("correctChoiceIndex", index);
    setErrorMessage(null);
  };

  return (
    <CustomModal
      visible={visible}
      onRequestClose={onClose}
      ariaLabel={modalTitle}
      style={styles.modal}
      bodyStyle={styles.modalBody}>
      <View style={styles.container}>
        <View style={styles.headerRow}>
          <CustomText text={modalTitle} useTranslationText={false} size="xl" weight="bold" style={styles.modalTitle} />
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            accessibilityRole="button"
            aria-label="Close question editor modal">
            <X size={18} color="#667085" />
          </TouchableOpacity>
        </View>

        <View style={styles.content}>
          <View style={styles.fieldGroup}>
            <CustomText text="Question" useTranslationText={false} weight="semiBold" style={styles.fieldLabel} />
            <View style={styles.inputWrap}>
              <Controller
                control={control}
                name="stem"
                render={({ field: { value, onChange } }) => (
                  <CustomTextInput
                    value={value}
                    onChangeText={onChange}
                    placeholder="Enter your question..."
                    multiline={true}
                    numberOfLines={4}
                    style={styles.questionInput}
                  />
                )}
              />
            </View>
          </View>

          <View style={styles.fieldGroup}>
            <View style={styles.answerHeaderRow}>
              <CustomText
                text="Answer Options"
                useTranslationText={false}
                weight="semiBold"
                style={styles.fieldLabel}
              />
              <CustomText
                text="Click letter to set correct answer"
                useTranslationText={false}
                style={styles.answerHint}
              />
            </View>

            <View style={styles.choiceRows}>
              {choiceFields.map((choiceField, index) => {
                const isCorrect = correctChoiceIndex === index;

                return (
                  <View key={choiceField.id} style={styles.choiceRow}>
                    <TouchableOpacity
                      style={[styles.choiceTag, isCorrect && styles.choiceTagCorrect]}
                      onPress={() => handleCorrectChoiceSelect(index)}
                      accessibilityRole="button"
                      aria-label={`Mark option ${optionLabel(index)} as correct answer`}>
                      <CustomText
                        text={optionLabel(index)}
                        useTranslationText={false}
                        weight="semiBold"
                        style={[styles.choiceTagText, isCorrect && styles.choiceTagTextCorrect]}
                      />
                    </TouchableOpacity>

                    <View style={styles.choiceInputWrap}>
                      <Controller
                        control={control}
                        name={`choices.${index}`}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <CustomTextInput
                            value={value ?? ""}
                            onChangeText={(inputValue) => {
                              onChange(inputValue);
                              setErrorMessage(null);
                            }}
                            onBlur={onBlur}
                            placeholder={`Option ${optionLabel(index)}`}
                          />
                        )}
                      />
                    </View>

                    <TouchableOpacity
                      style={[
                        styles.deleteOptionButton,
                        choiceFields.length <= MIN_CHOICES && styles.deleteOptionButtonDisabled,
                      ]}
                      onPress={() => handleRemoveOption(index)}
                      disabled={choiceFields.length <= MIN_CHOICES}
                      accessibilityRole="button"
                      aria-label={`Remove option ${optionLabel(index)}`}>
                      <Trash size={16} color={choiceFields.length <= MIN_CHOICES ? "#D0D5DD" : "#667085"} />
                    </TouchableOpacity>
                  </View>
                );
              })}
            </View>

            <CustomButton
              text="Add Option"
              useTranslationText={false}
              styleType="primaryTransparent"
              leftIcon={<Plus size={16} color="#3E68FE" />}
              onPress={handleAddOption}
              style={styles.addOptionButton}
              textStyle={styles.addOptionButtonText}
            />
          </View>

          <View style={styles.fieldGroup}>
            <CustomText
              text="Explanation (Optional)"
              useTranslationText={false}
              weight="semiBold"
              style={styles.fieldLabel}
            />
            <View style={styles.inputWrap}>
              <Controller
                control={control}
                name="explanation"
                render={({ field: { value, onChange } }) => (
                  <CustomTextInput
                    value={value}
                    onChangeText={onChange}
                    placeholder="Why is this answer correct?"
                    multiline={true}
                    numberOfLines={3}
                    style={styles.explanationInput}
                  />
                )}
              />
            </View>
          </View>

          <View style={styles.fieldGroup}>
            <CustomText
              text="Points (Optional)"
              useTranslationText={false}
              weight="semiBold"
              style={styles.fieldLabel}
            />
            <View style={styles.inputWrap}>
              <Controller
                control={control}
                name="points"
                render={({ field: { value, onChange } }) => (
                  <CustomTextInput
                    value={value}
                    onChangeText={(inputValue) => {
                      onChange(inputValue);
                      setErrorMessage(null);
                    }}
                    placeholder="e.g. 1"
                    keyboardType="numeric"
                  />
                )}
              />
            </View>
          </View>
        </View>

        {!!errorMessage && <CustomText text={errorMessage} useTranslationText={false} style={styles.errorText} />}

        <View style={styles.footer}>
          <CustomButton
            text="Cancel"
            useTranslationText={false}
            styleType="primaryTransparentStroke"
            onPress={onClose}
            disabled={isSubmitting}
            textStyle={styles.cancelButtonText}
            style={styles.cancelButton}
          />
          {isSubmitting ? (
            <View style={styles.submitButtonLoading}>
              <ActivityIndicator size="small" color="#FFFFFF" />
            </View>
          ) : (
            <CustomButton
              text={submitText}
              useTranslationText={false}
              size="s"
              onPress={handleSubmit(onSubmit)}
              styleType="primary"
              disabled={!canSubmit}
              textStyle={styles.submitButtonText}
              style={styles.submitButton}
            />
          )}
        </View>
      </View>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  modal: {
    maxWidth: 740,
    paddingHorizontal: 0,
    paddingVertical: 0,
    borderRadius: 18,
    overflow: "hidden",
  },
  modalBody: {
    marginTop: 0,
    flexGrow: 0,
  },
  container: {
    width: "100%",
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 28,
    paddingTop: 24,
    paddingBottom: 20,
    backgroundColor: "#F2F4F7",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  modalTitle: {
    color: "#101828",
  },
  closeButton: {
    width: 28,
    height: 28,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
  },
  content: {
    paddingHorizontal: 28,
    paddingTop: 20,
    paddingBottom: 20,
    gap: 20,
  },
  fieldGroup: {
    gap: 10,
  },
  fieldLabel: {
    color: "#344054",
  },
  inputWrap: {
    width: "100%",
  },
  questionInput: {
    minHeight: 96,
    textAlignVertical: "top",
    borderRadius: 10,
  },
  explanationInput: {
    minHeight: 92,
    textAlignVertical: "top",
    borderRadius: 10,
  },
  answerHeaderRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    flexWrap: "wrap",
  },
  answerHint: {
    color: "#667085",
    fontSize: 14,
  },
  choiceRows: {
    gap: 12,
  },
  choiceRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  choiceTag: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: "#EAECF0",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#EAECF0",
  },
  choiceTagCorrect: {
    backgroundColor: "#ECFDF3",
    borderColor: "#ABEFC6",
  },
  choiceTagText: {
    color: "#667085",
  },
  choiceTagTextCorrect: {
    color: "#027A48",
  },
  choiceInputWrap: {
    flex: 1,
  },
  deleteOptionButton: {
    width: 30,
    height: 30,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
  },
  deleteOptionButtonDisabled: {
    opacity: 0.5,
  },
  addOptionButton: {
    alignSelf: "flex-start",
    paddingHorizontal: 0,
    marginTop: 2,
  },
  addOptionButtonText: {
    fontWeight: "600",
  },
  errorText: {
    color: "#FF0000",
    paddingHorizontal: 28,
    paddingBottom: 8,
  },
  footer: {
    paddingHorizontal: 28,
    paddingTop: 18,
    paddingBottom: 22,
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cancelButton: {
    minWidth: 84,
    justifyContent: "center",
  },
  cancelButtonText: {
    color: "#475467",
    fontWeight: "600",
  },
  submitButton: {
    minWidth: 138,
    justifyContent: "center",
  },
  submitButtonText: {
    fontWeight: "700",
  },
  submitButtonLoading: {
    minWidth: 138,
    height: 36,
    borderRadius: 8,
    backgroundColor: "#3E68FE",
    alignItems: "center",
    justifyContent: "center",
  },
});

export default McqQuestionEditorModal;
